import { categories, newsList } from '@/mocks/news';
import type { Category, CategoryId, NewsArticle } from '@/types/news';

export const categoryColorMap: Record<CategoryId, string> = {
  society: 'primary',
  entertainment: 'secondary',
  life: 'accent',
};

export function getCategoryById(id: string): Category | undefined {
  return categories.find((item) => item.id === id || item.slug === id);
}

export function getCategoryName(id: CategoryId): string {
  return getCategoryById(id)?.name ?? '综合';
}

export function getNewsById(id: string): NewsArticle | undefined {
  return newsList.find((item) => item.id === id);
}

export function getNewsByCategory(id: string): NewsArticle[] {
  return newsList
    .filter((item) => item.category === id)
    .sort((a, b) => new Date(b.publishTime).getTime() - new Date(a.publishTime).getTime());
}

export function getHotNews(limit = 5): NewsArticle[] {
  return [...newsList].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getFeaturedNews(limit = 5): NewsArticle[] {
  const featured = newsList.filter((item) => item.hot);
  return featured.slice(0, limit);
}

export function getRelatedNews(article: NewsArticle, limit = 4): NewsArticle[] {
  const sameCategory = newsList.filter(
    (item) => item.id !== article.id && item.category === article.category,
  );
  const sameTag = newsList.filter(
    (item) =>
      item.id !== article.id &&
      item.category !== article.category &&
      item.tags.some((tag) => article.tags.includes(tag)),
  );
  return [...sameCategory, ...sameTag].slice(0, limit);
}

export function searchNews(keyword: string): NewsArticle[] {
  const key = keyword.trim().toLowerCase();
  if (!key) return [];
  return newsList
    .filter(
      (item) =>
        item.title.toLowerCase().includes(key) ||
        item.summary.toLowerCase().includes(key) ||
        item.tags.some((tag) => tag.toLowerCase().includes(key)) ||
        getCategoryName(item.category).includes(key),
    )
    .sort((a, b) => new Date(b.publishTime).getTime() - new Date(a.publishTime).getTime());
}

export function formatViews(views: number): string {
  if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
  return `${views}`;
}

export function formatDateTime(value: string): string {
  const date = new Date(value.replace(/-/g, '/'));
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  const hh = `${date.getHours()}`.padStart(2, '0');
  const mm = `${date.getMinutes()}`.padStart(2, '0');
  return `${y}-${m}-${d} ${hh}:${mm}`;
}

export function formatRelativeTime(value: string): string {
  const target = new Date(value.replace(/-/g, '/')).getTime();
  const now = new Date('2026/10/12 12:00:00').getTime();
  const diff = Math.max(now - target, 0);
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diff < hour) {
    const mins = Math.max(Math.floor(diff / minute), 1);
    return `${mins}分钟前`;
  }
  if (diff < day) return `${Math.floor(diff / hour)}小时前`;
  if (diff < 7 * day) return `${Math.floor(diff / day)}天前`;
  return formatDateTime(value).slice(0, 10);
}